/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);
      this.linkedVariantClickHandler = this.handleLinkedVariantClick.bind(this);
      this.thumbClickHandler = this.handleThumbClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('click', this.linkedVariantClickHandler);
      this.addEventListener('click', this.thumbClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      this.removeEventListener('click', this.linkedVariantClickHandler);
      this.removeEventListener('click', this.thumbClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Gets the URL to fetch product details from. React PDP and Native PDP templates render no markup the drawer
     * can extract, so those cards expose a `data-quick-add-url` pointing at the classic rendering.
     * @param {Element} el - Quick add button.
     * @returns {string}
     */
    getFetchUrl(el) {
      if (!el) return '';
      return el.dataset.quickAddUrl ||
             el.getAttribute('data-quick-add-url') ||
             el.closest('[data-quick-add-url]')?.dataset.quickAddUrl ||
             el.closest('[data-quick-add-url]')?.getAttribute('data-quick-add-url') ||
             el.dataset.productUrl ||
             el.getAttribute('data-product-url');
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      const fetchUrl = this.getFetchUrl(evt.target);
      if (fetchUrl && !this.fetchedUrls.includes(fetchUrl)) {
        this.fetch = {
          url: fetchUrl,
          promise: fetch(fetchUrl)
        };
        this.fetchedUrls.push(fetchUrl);
      }
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        // this.cartDrawer.open();
        if (typeof handleFloCartBtn === 'function') handleFloCartBtn();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      const opener = evt.target.closest('.js-quick-add, .quick-add__submit, [data-gtm-quick-view]');
      if (!opener) return;

      evt.preventDefault();
      evt.stopPropagation();

      // If drawer is already open and click is from outside, close current drawer
      if (this.hasAttribute('open') && !this.contains(opener)) {
        this.close();
        return;
      }

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(opener);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(opener);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });
    }

    /**
     * Intercepts clicks on the Color/Type/Size "linked product" swatches
     * (see snippets/product-linked-variants.liquid) while the Quick View
     * drawer is open, so picking a different size swaps the product shown
     * INSIDE the drawer instead of navigating the browser away to that
     * product's page.
     * @param {object} evt - Event object.
     */
    handleLinkedVariantClick(evt) {
      const link = evt.target.closest('.product-linked-variants a.custom-size-variant');
      if (!link) return;

      evt.preventDefault();
      evt.stopPropagation();

      const href = link.getAttribute('href');
      if (href) this.switchLinkedVariant(href);
    }

    /**
     * Swaps the product shown in an already-open Quick View drawer to a
     * different ("linked") product, without closing/reopening the drawer or
     * navigating the page. Mirrors `open()`, minus opening the drawer itself.
     * @param {string} url - URL of the product to switch to.
     */
    async switchLinkedVariant(url) {
      if (!url || url === this.productUrl) return;

      // Lock BOTH the content height and the drawer's own height as inline styles.
      // Inline styles beat any CSS rule (including the mobile is-loading { min-height: 50vh })
      // so the drawer can't collapse or jump while the new variant is fetched.
      const currentHeight = this.content.offsetHeight;
      if (currentHeight > 0) this.content.style.minHeight = currentHeight + 'px';

      const drawerHeight = this.offsetHeight;
      if (drawerHeight > 0) this.style.minHeight = drawerHeight + 'px';

      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      const fetchUrl = url + (url.indexOf('?') > -1 ? '&' : '?') + 'view=classic';

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) {
          this.content.style.minHeight = '';
          this.style.minHeight = '';
          window.location.href = url;
          return;
        }

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const productEl = tmpl.content.querySelector('.js-product') ||
                           tmpl.content.querySelector('.product') ||
                           tmpl.content.querySelector('main');

        if (!productEl) {
          this.content.style.minHeight = '';
          this.style.minHeight = '';
          window.location.href = url;
          return;
        }

        this.productUrl = url;
        this.productEl = productEl;
        this.fetchedUrls.push(fetchUrl);
        this.renderProduct({ dataset: {} });
      } catch (err) {
        console.error('Quick Add switch error:', err);
        this.content.style.minHeight = '';
        this.style.minHeight = '';
        window.location.href = url;
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      const productUrl = opener.dataset.productUrl ||
                         opener.getAttribute('data-product-url') ||
                         opener.closest('[data-product-url]')?.dataset.productUrl ||
                         (opener.closest('.st-product-card, .st-product-wrap, .card, .product-card')?.querySelector('a[href*="/products/"]')?.getAttribute('href'))?.split('?')[0];

      if (!productUrl) {
        opener.removeAttribute('aria-disabled');
        return;
      }

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === productUrl && this.productEl) {
        super.open(opener);
        if (opener.dataset.selectedColor) this.setActiveVariant(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      const fetchUrl = this.getFetchUrl(opener) || (productUrl + (productUrl.includes('?') ? '&' : '?') + 'view=classic');

      if (!this.fetch || this.fetch.url !== fetchUrl) {
        this.fetch = {
          url: fetchUrl,
          promise: fetch(fetchUrl)
        };
      }

      try {
        const response = await this.fetch.promise;
        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();
          this.productEl = tmpl.content.querySelector('.js-product') ||
                           tmpl.content.querySelector('.product') ||
                           tmpl.content.querySelector('main');
          if (this.productEl) {
            this.renderProduct(opener);
          } else {
            window.location.href = productUrl;
          }
        } else {
          window.location.href = productUrl;
        }
      } catch (err) {
        console.error('Quick Add fetch error:', err);
        window.location.href = productUrl;
      }

      this.fetch = null;
      opener.removeAttribute('aria-disabled');
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      if (!this.productEl) return;

      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset ? this.productEl.dataset.section : null;
      if (sectionId) {
        this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');
      }

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) variantPicker.dataset.updateUrl = 'false';

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      this.updateMedia(activeMedia ? activeMedia.dataset.mediaId : null);

      if (opener.dataset.selectedColor) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Escapes a string for safe use inside an HTML attribute / text node.
     * @param {string} value - Raw string.
     * @returns {string}
     */
    escapeHtml(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    /**
     * Returns a Shopify CDN image URL resized to the given width.
     * @param {string} src - Original image URL.
     * @param {number} width - Wanted width in px.
     * @returns {string}
     */
    imageUrl(src, width) {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', width);
        url.searchParams.delete('height');
        url.searchParams.delete('crop');
        return url.toString();
      } catch (err) {
        return src;
      }
    }

    /**
     * Reads the real (non-placeholder) source of a product image, lazy-loaded or not.
     * @param {?Element} img - The image element.
     * @returns {string}
     */
    getImageSource(img) {
      if (!img) return '';
      const candidates = [img.getAttribute('data-src'), img.getAttribute('src')];
      return candidates.find((value) => value && !value.startsWith('data:')) || '';
    }

    /**
     * Collects every still image of the fetched product (used for the main image + thumbnails).
     * @returns {Array<{id: string, src: string, alt: string, current: boolean}>}
     */
    getMediaItems() {
      const items = [];

      this.productEl.querySelectorAll('.media-viewer__item').forEach((li) => {
        const type = li.dataset.mediaType;
        if (type && type !== 'image') return;

        const img = li.querySelector('img.product-image') || li.querySelector('img');
        const src = this.getImageSource(img);
        if (!src) return;

        items.push({
          id: li.dataset.mediaId || String(items.length),
          src,
          alt: img.getAttribute('alt') || '',
          current: li.classList.contains('is-current-variant')
        });
      });

      // Fallback for templates that don't use the media gallery markup.
      if (!items.length) {
        const img = this.productEl.querySelector('#product-media img, .product-media img, img');
        const src = this.getImageSource(img);
        if (src) items.push({ id: 'single', src, alt: img.getAttribute('alt') || '', current: true });
      }

      return items;
    }

    /**
     * Updates the main product image (and the active thumbnail).
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      if (!this.productEl) return;

      const items = this.mediaItems && this.mediaItems.length ? this.mediaItems : this.getMediaItems();
      let item = mediaId ? items.find((i) => String(i.id) === String(mediaId)) : null;
      if (!item) item = items.find((i) => i.current) || items[0];

      const container = this.querySelector('.quick-add-info__media');
      if (!item || !container) return;

      container.innerHTML = `
        <img src="${this.escapeHtml(this.imageUrl(item.src, 400))}"
             srcset="${this.escapeHtml(this.imageUrl(item.src, 400))} 1x, ${this.escapeHtml(this.imageUrl(item.src, 800))} 2x"
             width="400" height="400" alt="${this.escapeHtml(item.alt)}">
      `;

      this.querySelectorAll('.qv-thumb').forEach((thumb) => {
        const isActive = thumb.dataset.mediaId === String(item.id);
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    /**
     * Handles clicks on the thumbnail strip.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('.qv-thumb');
      if (!thumb || !this.contains(thumb)) return;

      evt.preventDefault();
      this.updateMedia(thumb.dataset.mediaId);
    }

    /**
     * Builds the star rating row. The data comes from the hidden
     * `.js-quickview-rating` element rendered by snippets/product-linked-variants.liquid.
     * @returns {string}
     */
    getRatingHtml() {
      const el = this.productEl.querySelector('.js-quickview-rating');
      if (!el) return '';

      const average = parseFloat(el.dataset.average);
      const count = parseInt(el.dataset.count, 10);
      if (!average || isNaN(average) || average <= 0) return '';

      const percent = Math.max(0, Math.min(100, (average / 5) * 100));
      return `
        <div class="qv-rating">
          <span class="qv-stars" style="--qv-rating: ${percent}%" role="img" aria-label="${average.toFixed(1)} out of 5 stars"></span>
          ${count > 0 ? `<span class="qv-rating__count">(${count})</span>` : ''}
        </div>
      `;
    }

    /**
     * Builds the thumbnail strip markup (only when the product has more than one image).
     * @returns {string}
     */
    getThumbsHtml() {
      if (!this.mediaItems || this.mediaItems.length < 2) return '';

      const thumbs = this.mediaItems.map((item, index) => `
        <button type="button" class="qv-thumb" data-media-id="${this.escapeHtml(item.id)}" aria-label="Show image ${index + 1}" aria-current="false">
          <span class="qv-thumb__inner">
            <img src="${this.escapeHtml(this.imageUrl(item.src, 120))}" width="120" height="120" loading="lazy" alt="">
          </span>
        </button>
      `).join('');

      return `<div class="qv-thumbs">${thumbs}</div>`;
    }

    /**
     * Builds the markup for the drawer content element.
     *
     * Layout: [image | title, rating, price, "View details"] -> thumbnails -> divider -> options.
     */
    updateContent() {
      this.mediaItems = this.getMediaItems();

      // Linked colour/type/size "variant" swatches (separate products used as
      // variants via metafields). Same markup + behaviour as the PDP - each
      // swatch is a normal link to that product's page. Only rendered when the
      // product actually has any, so products without this metafield setup
      // don't get stray empty spacing.
      const linkedVariantsHtml = this.getElementHtml('.product-linked-variants');
      const linkedVariantsBlock = linkedVariantsHtml
        ? `<div class="product-linked-variants qv-linked-variants">${linkedVariantsHtml}</div>`
        : '';

      const optionsHtml = this.getElementHtml('.product-options');
      const hasOptions = Boolean(linkedVariantsHtml || (optionsHtml && optionsHtml.trim()));

      this.content.innerHTML = `
        <div class="qv-summary">
          <div class="quick-add-info__media qv-media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="qv-details">
            <div class="product-title qv-title">
              <a class="js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${this.getRatingHtml()}
            <div class="product-price qv-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <a href="${this.productUrl}" class="qv-view-details js-prod-link">
              ${theme.strings.viewDetails}
            </a>
          </div>
        </div>
        ${this.getThumbsHtml()}
        ${hasOptions ? '<hr class="qv-divider">' : ''}
        ${linkedVariantsBlock}
        <div class="product-options">
          ${optionsHtml}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');

      // Release both height locks one paint after the new content is in the DOM.
      requestAnimationFrame(() => {
        this.content.style.minHeight = '';
        this.style.minHeight = '';
      });
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of an element within the product element.
     * @param {string} selector - CSS selector for the element.
     * @returns {?string}
     */
    getElementHtml(selector) {
      const el = this.productEl.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);
      }
      this.close();
      setTimeout(() => {
        this.cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');
        if (typeof handleFloCartBtn === 'function') {
          handleFloCartBtn();
        } else if (this.cartDrawer && typeof this.cartDrawer.open === 'function') {
          this.cartDrawer.open();
        }
      }, 100);
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor || 2000);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
